<template>
  <CustomModal
    :title="title"
    :visible="visible"
    :width="'32vw'"
    @update:visible="$emit('update:visible', $event)"
  >
    <div class="flex flex-col gap-6">
      <span>{{ description }}</span>
      <div id="email" class="form_col">
        <span class="secondary_color text-[13px]">Email ID</span>
        <CustomInput
          format="text"
          v-model="exportEmail"
          placeholder="Enter Email"
          type="primary"
          :is-error="exportEmailError !== ''"
          :error-message="exportEmailError"
          :on-change="() => (exportEmailError = '')"
        />
      </div>
    </div>

    <template #footer>
      <a-button
        class="primary_medium"
        @click="handleExport"
        :loading="exporting"
      >
        Export
      </a-button>
    </template>
  </CustomModal>
</template>

<script setup>
import { ref, watch } from "vue";
import { useRouter } from "vue-router";
import CustomModal from "@/src/components/CustomModal.vue";
import CustomInput from "@/src/components/CustomInput.vue";
import { validateEmail, showExportMessage } from "@/src/utils/utils.js";
import { message } from "ant-design-vue";

const router = useRouter();

const props = defineProps({
  visible: { type: Boolean, required: true },
  title: { type: String, default: "Export Data" },
  description: {
    type: String,
    default: "Selected data will be exported to your email",
  },
  selection: { type: Object, required: true },
  exportFunction: { type: Function, required: true },
});

const emit = defineEmits(["update:visible", "export-success"]);

const exportEmail = ref("");
const exportEmailError = ref("");
const exporting = ref(false);

const handleExport = async () => {
  if (!exportEmail.value.trim()) {
    exportEmailError.value = "Please enter an email address";
    return;
  }

  if (!validateEmail(exportEmail.value)) {
    exportEmailError.value = "Please enter a valid email address";
    return;
  }

  if (
    props.selection.selected_ids.length === 0 &&
    !props.selection.select_all
  ) {
    message.error("Please select items to export");
    return;
  }

  try {
    exporting.value = true;
    const exportData = {
      select_all: props.selection.select_all,
      selected_ids: props.selection.selected_ids,
      unselected_ids: props.selection.unselected_ids,
      export_email: exportEmail.value,
    };

    const response = await props.exportFunction(exportData);

    if (response.success) {
      const exportCount = props.selection.select_all
        ? "Selected items"
        : `${props.selection.selected_ids.length} item${props.selection.selected_ids.length !== 1 ? "s" : ""}`;

      showExportMessage(router, "", exportCount);
      emit("update:visible", false);
      emit("export-success");
      exportEmail.value = "";
      exportEmailError.value = "";
    } else {
      message.error("Failed to export data. Please try again.");
    }
  } catch (error) {
    console.error("Error exporting data:", error);
    message.error("An error occurred while exporting data");
  } finally {
    exporting.value = false;
  }
};

watch(
  () => props.visible,
  (newVisible) => {
    if (newVisible) {
      exportEmail.value = localStorage.getItem("userEmail") || "";
      exportEmailError.value = "";
    }
  }
);
</script>
